import React, { useCallback, useState, type FC } from "react";
import { Modal } from "react-native";
import { View, Text, Button } from "@src/components";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useAuth0Upsell } from "@src/hooks/useAuth0Upsell";
import { useAuth0Login } from "@src/hooks/useAuth0Login";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import LogoIcon from "@src/assets/svgs/logo-icon.svg";
import LogoType from "@src/assets/svgs/logo-type.svg";

/**
 * Hard upsell modal component that blocks the app until the user upgrades to Auth0.
 * Similar to the ForceUpgradeModal but for auth migration.
 * Content is controlled via Amplitude feature flag.
 */
export const Auth0UpsellModal: FC = () => {
  const { theme } = useUnistyles();
  const { config, showHardModal, isOnLegacyFlow } = useAuth0Upsell();
  const { initiateAuth, isReady } = useAuth0Login();
  const { track } = useAmplitude();
  const isDarkMode = theme.currentTheme === "dark";
  const [isLoading, setIsLoading] = useState(false);
  // Temporarily hide modal while Auth0 browser is open to avoid iOS modal presentation conflict
  const [isAuthBrowserOpen, setIsAuthBrowserOpen] = useState(false);

  const handleUpgradePress = useCallback(async () => {
    track(ANALYTICS_EVENTS.AUTH0_UPSELL_BANNER_CLICKED, {
      mode: "hard",
      title: config.title,
    });

    setIsLoading(true);
    // Temporarily hide the modal while Auth0 browser is open to avoid iOS modal presentation conflict
    setIsAuthBrowserOpen(true);

    // Small delay to ensure the modal is dismissed before Auth0 browser opens
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      // Directly initiate Auth0 login flow - same as the soft banner
      // This will open the Auth0 browser/modal and handle token exchange automatically
      await initiateAuth();
      // If we get here, auth succeeded - showHardModal will become false via accessToken being set
    } catch (error) {
      console.error("[Auth0UpsellModal] Auth initiation failed:", error);
    } finally {
      // Auth browser is closed (either success, cancel, or error)
      // Show the modal again - if auth succeeded, showHardModal will be false anyway
      setIsAuthBrowserOpen(false);
      setIsLoading(false);
    }
  }, [track, config.title, initiateAuth]);

  // Don't render the modal when:
  // 1. showHardModal is false (user migrated or feature flag off)
  // 2. Auth0 browser is open (to avoid iOS modal presentation conflict)
  if (!showHardModal || isAuthBrowserOpen) {
    return null;
  }

  return (
    <Modal
      visible={true}
      animationType="fade"
      transparent={true}
      onRequestClose={() => {
        // Do nothing - modal cannot be dismissed
      }}
    >
      {/* Semi-transparent dark overlay */}
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          justifyContent: "center",
          alignItems: "center",
          padding: theme.spacing["4xl"],
        }}
      >
        {/* Alert-style modal content */}
        <View
          style={{
            backgroundColor: theme.colors.background,
            borderRadius: theme.spacing.xl,
            padding: theme.spacing["4xl"],
            width: "85%",
            alignItems: "center",
            shadowColor: "#000",
            shadowOffset: {
              width: 0,
              height: 2,
            },
            shadowOpacity: 0.25,
            shadowRadius: 4,
            elevation: 5,
          }}
        >
          {/* Logo */}
          <View flexDirection="row" alignItems="center" mb="2xl">
            <LogoIcon width={55} height={50} />
            <LogoType width={115} height={50} style={styles.logoType} />
          </View>

          {/* Title */}
          <Text
            textStyle="h4"
            style={{
              textAlign: "center",
              marginBottom: theme.spacing["2xl"],
            }}
          >
            {config.title}
          </Text>

          {/* Message */}
          <Text
            textStyle="sm/regular"
            style={{
              textAlign: "center",
              color: theme.colors.textMuted,
              marginBottom: theme.spacing["4xl"],
              lineHeight: 20,
            }}
          >
            {config.message}
          </Text>

          {/* Upgrade Button */}
          <Button
            onPress={handleUpgradePress}
            size="md"
            variant="primary"
            label={config.ctaText}
            textStyle="sm/medium"
            loading={isLoading}
            disabled={isLoading || !isReady}
          />

          {/* Small note */}
          <Text
            textStyle="sm/regular"
            style={{
              textAlign: "center",
              color: theme.colors.textMuted,
              marginTop: theme.spacing["2xl"],
            }}
          >
            Required to continue
          </Text>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create((theme) => ({
  logoType: {
    color: theme.colors.text,
  },
}));
